"use client"

import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"
import { cn } from "@/lib/utils"

// ─── Size map ───────────────────────────────────────────────────────────────
const sizeMap = {
  sm:   "max-w-sm",
  md:   "max-w-[670px] w-[90dvw]",
  lg:   "max-w-[992px] w-[90dvw]",
  xl:   "max-w-4xl",
  full: "max-w-[95vw]",
}

// ─── Props ──────────────────────────────────────────────────────────────────
export interface AppDialogProps {
  open?: boolean
  onOpenChange?: (open: boolean) => void
  /** Dialog width preset */
  size?: keyof typeof sizeMap
  title?: React.ReactNode
  description?: React.ReactNode
  children?: React.ReactNode
  footer?: React.ReactNode
  /** Hide the close button */
  hideClose?: boolean
  className?: string
}

// ─── Component ──────────────────────────────────────────────────────────────
export function AppDialog({
  open,
  onOpenChange,
  size = "md",
  title,
  description,
  children,
  footer,
  hideClose = false,
  className,
}: AppDialogProps) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        {/* Overlay */}
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/60 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />

        {/* Content */}
        <DialogPrimitive.Content
          className={cn(
            "fixed left-1/2 top-1/2 z-50 w-full -translate-x-1/2 -translate-y-1/2",
            "bg-background border border-border rounded-2xl shadow-xl",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 overflow-hidden",
            sizeMap[size],
            className
          )}
        >
          {/* Header */}
          {title && (
            <div className="flex items-center justify-between px-4 md:px-6 py-4 border-b border-border">
              <DialogPrimitive.Title className="text-lg font-semibold text-foreground md:text-center flex-1">
                {title}
              </DialogPrimitive.Title>
              {!hideClose && (
                <DialogPrimitive.Close className="rounded-full border border-primary p-1 text-primary hover:text-foreground transition-colors focus:outline-none focus:ring-2 focus:ring-ring">
                  <X className="h-4 w-4" />
                  <span className="sr-only">Close</span>
                </DialogPrimitive.Close>
              )}
            </div>
          )}

          {/* Body */}
          <div className="px-4 md:px-6 py-6 max-h-[calc(80dvh-120px)] overflow-auto">
            {description && (
              <DialogPrimitive.Description className="sr-only">
                {description}
              </DialogPrimitive.Description>
            )}
            {children}
          </div>

          {/* Footer */}
          {footer && (
            <div className="px-4 md:px-6 pb-4 md:pb-6 pt-4 bg-background ">
              {footer}
            </div>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
